import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BookOpen, Loader2, Pause, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { getBookName } from '../utils/bookTranslations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bible/intro`;

const L = {
  en: { intro: 'Intro', book: 'Book', chapter: 'Chapter', author: 'Author', date: 'Date', audience: 'Audience', purpose: 'Purpose',
    topics: 'Key topics', verses: 'Key verses', christ: 'Christ in this book', outline: 'Outline', keyVerse: 'Key verse',
    original: 'Torres Amat (1823) summary', preface: 'Torres Amat (1823) introduction', none: 'No introduction available yet.', more: ['Read more', 'Show less'], listen: 'Listen', pause: 'Pause' },
  es: { intro: 'Intro', book: 'Libro', chapter: 'Capítulo', author: 'Autor', date: 'Fecha', audience: 'Destinatarios', purpose: 'Propósito',
    topics: 'Temas clave', verses: 'Versículos clave', christ: 'Cristo en este libro', outline: 'Bosquejo', keyVerse: 'Versículo clave',
    original: 'Resumen de Torres Amat (1823)', preface: 'Introducción de Torres Amat (1823)', none: 'Aún no hay introducción disponible.', more: ['Leer más', 'Ver menos'], listen: 'Escuchar', pause: 'Pausa' },
};

const Field = ({ label, children, testId }) => (children ? (
  <div className="space-y-1" data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">{label}</p>
    <div className="text-sm leading-relaxed text-foreground/90">{children}</div>
  </div>
) : null);

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {(items || []).map((t) => (
      <span key={t} className="rounded-full bg-amber-100/70 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs text-amber-900 dark:text-amber-200">{t}</span>
    ))}
  </div>
);

const Preface = ({ text, label, more }) => {
  const [full, setFull] = useState(false);
  const long = text.length > 1200;
  return (
    <Field label={label} testId="intro-ta-preface">
      <p className="italic whitespace-pre-line">{full || !long ? text : `${text.slice(0, 1200)}…`}</p>
      {long && (
        <button type="button" onClick={() => setFull((v) => !v)} data-testid="intro-preface-toggle"
          className="mt-1 text-xs font-medium text-amber-700 hover:underline dark:text-amber-400">{full ? more[1] : more[0]}</button>
      )}
    </Field>
  );
};

const BookTab = ({ data, t }) => {
  const b = data?.book_intro;
  const pre = data?.original_preface;
  if (!b && !pre) return <p className="text-sm text-muted-foreground">{t.none}</p>;
  return (
    <div className="space-y-4" data-testid="intro-book-tab">
      {b?.summary && <p className="text-sm leading-relaxed">{b.summary}</p>}
      <Field label={t.author} testId="intro-author">{b?.author}</Field>
      <Field label={t.date}>{b?.date}</Field>
      <Field label={t.audience}>{b?.audience}</Field>
      <Field label={t.purpose}>{b?.purpose}</Field>
      {b?.key_topics?.length > 0 && <Field label={t.topics}><Chips items={b.key_topics} /></Field>}
      {b?.key_verses?.length > 0 && <Field label={t.verses}><ul className="list-disc pl-4 space-y-0.5">{b.key_verses.map((v) => <li key={v}>{v}</li>)}</ul></Field>}
      <Field label={t.christ}>{b?.christ_in_book}</Field>
      {b?.outline?.length > 0 && (
        <Field label={t.outline}>
          <ul className="space-y-1">{b.outline.map((o) => (
            <li key={`${o.range}-${o.title}`} className="flex gap-2"><span className="shrink-0 font-mono text-xs text-amber-700 dark:text-amber-400 pt-0.5">{o.range}</span><span>{o.title}</span></li>
          ))}</ul>
        </Field>
      )}
      {pre?.text && <Preface text={pre.text} label={t.preface} more={t.more} />}
    </div>
  );
};

const ChapterTab = ({ data, t }) => {
  const c = data?.chapter_intro;
  const o = data?.original_summary;
  if (!c && !o) return <p className="text-sm text-muted-foreground">{t.none}</p>;
  return (
    <div className="space-y-4" data-testid="intro-chapter-tab">
      {c?.summary && <p className="text-sm leading-relaxed">{c.summary}</p>}
      {c?.key_topics?.length > 0 && <Field label={t.topics}><Chips items={c.key_topics} /></Field>}
      <Field label={t.keyVerse}>{c?.key_verse}</Field>
      {o?.text && <Field label={t.original} testId="intro-ta-summary"><p className="italic">{o.text}</p></Field>}
    </div>
  );
};

const IntroAudio = ({ book, chapter, lang, t, open }) => {
  const ref = useRef(null);
  const [state, setState] = useState('idle');
  useEffect(() => {
    const a = ref.current;
    if (a) { a.pause(); a.removeAttribute('src'); }
    setState('idle');
  }, [book, chapter, lang, open]);
  const toggle = () => {
    const a = ref.current;
    if (state === 'playing') { a.pause(); setState('paused'); return; }
    if (!a.getAttribute('src')) {
      a.src = `${API}/audio?book=${encodeURIComponent(book)}&chapter=${chapter}&lang=${lang}`;
      setState('loading');
    }
    a.play().then(() => setState('playing')).catch(() => setState('idle'));
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={toggle} disabled={state === 'loading'} data-testid="intro-audio-btn"
        className="h-8 gap-1.5 rounded-full border-amber-300/70 text-amber-800 dark:border-amber-700/60 dark:text-amber-300">
        {state === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : state === 'playing' ? <Pause className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        {state === 'playing' ? t.pause : t.listen}
      </Button>
      <audio ref={ref} onPlaying={() => setState('playing')} onEnded={() => setState('idle')} onError={() => setState('idle')} preload="none" data-testid="intro-audio" />
    </>
  );
};

export const BibleIntroButton = ({ book, chapter, translation, language }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lang = language === 'es' ? 'es' : 'en';
  const t = L[lang];

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(API, { params: { book, chapter, lang, translation } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, book, chapter, lang, translation]);

  const bookName = getBookName(book, lang);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="chapter-intro-btn"
        className="mb-3 h-8 gap-1.5 rounded-full border-amber-300/70 text-amber-800 hover:bg-amber-50 dark:border-amber-700/60 dark:text-amber-300 dark:hover:bg-amber-900/20">
        <BookOpen className="h-3.5 w-3.5" /> {t.intro}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="chapter-intro-dialog">
          <DialogHeader className="pr-10 flex-row items-center justify-between gap-3 space-y-0">
            <DialogTitle>{bookName} {chapter}</DialogTitle>
            {data?.chapter_intro && <IntroAudio book={book} chapter={chapter} lang={lang} t={t} open={open} />}
          </DialogHeader>
          {loading ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-600" /> : (
            <Tabs defaultValue={chapter === 1 ? 'book' : 'chapter'}>
              <TabsList className="flex w-full">
                <TabsTrigger value="book" className="flex-1" data-testid="intro-tab-book">{t.book}</TabsTrigger>
                <TabsTrigger value="chapter" className="flex-1" data-testid="intro-tab-chapter">{t.chapter} {chapter}</TabsTrigger>
              </TabsList>
              <TabsContent value="book" className="pt-3"><BookTab data={data} t={t} /></TabsContent>
              <TabsContent value="chapter" className="pt-3"><ChapterTab data={data} t={t} /></TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
